import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import SchedulerService from "../../services/SchedulerService";
import HttpService from "../../services/HttpService";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import TuneIcon from "@mui/icons-material/Tune";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import RenderOnRole from "../../components/RenderOnRole";

const STATUS_COLORS = {
  ACTIVE: "success",
  DRAFT: "warning",
  ARCHIVED: "default",
};

const TYPE_COLORS = {
  CUSTOMER_JOURNEY: "primary",
  ONBOARDING: "secondary",
};

export default function SchedulerTemplates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [createDlgOpen, setCreateDlgOpen] = useState(false);
  const [editDlg, setEditDlg] = useState(null); // null or template object
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const [form, setForm] = useState({
    name: "",
    scheduleType: "CUSTOMER_JOURNEY",
    description: "",
    totalDays: 7,
    idEmployer: "ANY",
  });

  const { data: employers = [] } = useQuery(["employers-all"], () =>
    HttpService.getAxiosClient()
      .get(window.API_URL + "/employer/all?excludeRelations=true")
      .then((r) => r.data)
  );

  const { data, isLoading } = useQuery(
    ["scheduler-templates"],
    () => SchedulerService.getTemplates(),
    { select: (r) => r.data }
  );

  const createMutation = useMutation(
    (body) => SchedulerService.createTemplate(body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scheduler-templates"]);
        setCreateDlgOpen(false);
        resetForm();
        showFeedback("success", "Template created");
      },
      onError: () => showFeedback("error", "Failed to create template"),
    }
  );

  const updateMutation = useMutation(
    ({ id, body }) => SchedulerService.updateTemplate(id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scheduler-templates"]);
        setEditDlg(null);
        showFeedback("success", "Template saved");
      },
      onError: () => showFeedback("error", "Failed to save template"),
    }
  );

  const cloneMutation = useMutation(
    (id) => SchedulerService.cloneTemplate(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["scheduler-templates"]);
        showFeedback("success", "Template cloned");
      },
      onError: () => showFeedback("error", "Failed to clone template"),
    }
  );

  const showFeedback = (type, message) =>
    setFeedback({ open: true, type, message });

  const resetForm = () =>
    setForm({
      name: "",
      scheduleType: "CUSTOMER_JOURNEY",
      description: "",
      totalDays: 7,
      idEmployer: "ANY",
    });

  const openEditDlg = (row) => {
    setForm({
      name: row.name,
      scheduleType: row.scheduleType,
      description: row.description || "",
      totalDays: row.totalDays,
      status: row.status,
      idEmployer: row.idEmployer || "ANY",
    });
    setEditDlg(row);
  };

  const columns = [
    {
      field: "name",
      headerName: "Template Name",
      flex: 2,
      minWidth: 180,
    },
    {
      field: "scheduleType",
      headerName: "Type",
      width: 180,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={
            value === "CUSTOMER_JOURNEY" ? "Customer Journey" : "Onboarding"
          }
          color={TYPE_COLORS[value] || "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "idEmployer",
      headerName: "Employer",
      width: 200,
      renderCell: ({ value }) => {
        const employer = employers.find((e) => e.idEmployer === value);
        return value === "ANY" ? (
          <Chip size="small" label="System (Any)" color="info" />
        ) : (
          <Typography variant="body2">
            {employer ? `${employer.name} (${value})` : value}
          </Typography>
        );
      },
    },
    {
      field: "totalDays",
      headerName: "Days",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "version",
      headerName: "Version",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value}
          color={STATUS_COLORS[value] || "default"}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Edit template">
            <IconButton size="small" onClick={() => openEditDlg(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Manage days & tasks">
            <IconButton
              size="small"
              onClick={() =>
                navigate(
                  `/communication/scheduler/templates/${row.idScheduleTemplate}`,
                  {
                    state: { template: row },
                  }
                )
              }
            >
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clone template">
            <IconButton
              size="small"
              onClick={() => cloneMutation.mutate(row.idScheduleTemplate)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">Schedule Templates</Typography>
        <RenderOnRole roles={["scheduler-admin", "pc-program-super-admin"]}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setCreateDlgOpen(true);
            }}
          >
            New Template
          </Button>
        </RenderOnRole>
      </Box>

      {/* Grid */}
      <DataGrid
        autoHeight
        rows={data || []}
        columns={columns}
        getRowId={(r) => r.idScheduleTemplate}
        loading={isLoading}
        components={{ Toolbar: GridToolbar }}
        componentsProps={{ toolbar: { showQuickFilter: true } }}
        rowsPerPageOptions={[10, 25, 50]}
        initialState={{ pagination: { pageSize: 10 } }}
        disableSelectionOnClick
      />

      {/* ── Create dialog ─── */}
      <Dialog
        open={createDlgOpen}
        onClose={() => setCreateDlgOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          New Schedule Template
          <IconButton
            onClick={() => setCreateDlgOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TemplateForm form={form} setForm={setForm} employers={employers} />
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => createMutation.mutate(form)}
              disabled={!form.name || createMutation.isLoading}
            >
              Create
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Edit dialog ─── */}
      <Dialog
        open={!!editDlg}
        onClose={() => setEditDlg(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Template
          <IconButton
            onClick={() => setEditDlg(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TemplateForm
            form={form}
            setForm={setForm}
            showStatus
            employers={employers}
          />
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() =>
                updateMutation.mutate({
                  id: editDlg.idScheduleTemplate,
                  body: {
                    name: form.name,
                    scheduleType: form.scheduleType,
                    description: form.description,
                    totalDays: form.totalDays,
                    status: form.status,
                    idEmployer: form.idEmployer,
                  },
                })
              }
              disabled={!form.name || updateMutation.isLoading}
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Feedback */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback.type}
          onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function TemplateForm({ form, setForm, showStatus, employers = [] }) {
  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <Grid container spacing={2} mt={0.5}>
      <Grid item xs={12}>
        <TextField
          label="Template Name"
          value={form.name}
          onChange={set("name")}
          fullWidth
          required
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          options={[
            { idEmployer: "ANY", name: "System (Any Employer)" },
            ...employers,
          ]}
          getOptionLabel={(e) =>
            e.idEmployer === "ANY"
              ? "System (Any Employer)"
              : `${e.name} (${e.idEmployer})`
          }
          isOptionEqualToValue={(opt, val) => opt.idEmployer === val.idEmployer}
          value={
            form.idEmployer === "ANY"
              ? { idEmployer: "ANY", name: "System (Any Employer)" }
              : employers.find((e) => e.idEmployer === form.idEmployer) || {
                  idEmployer: form.idEmployer,
                  name: form.idEmployer,
                }
          }
          onChange={(_, val) => {
            setForm((f) => ({
              ...f,
              idEmployer: val ? val.idEmployer : "ANY",
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Employer"
              required
              helperText="Select which employer this template belongs to"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Schedule Type</InputLabel>
          <Select
            value={form.scheduleType || "CUSTOMER_JOURNEY"}
            label="Schedule Type"
            onChange={set("scheduleType")}
          >
            <MenuItem value="CUSTOMER_JOURNEY">Customer Journey</MenuItem>
            <MenuItem value="ONBOARDING-PREAUTHORIZED">
              Onboarding - Pay Any-Day
            </MenuItem>
            <MenuItem value="ONBOARDING-DISBURSEMENT">
              Onboarding - Disbursement
            </MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Total Days"
          type="number"
          inputProps={{ min: 1, max: 365 }}
          value={form.totalDays}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              totalDays: parseInt(e.target.value, 10) || 1,
            }))
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Description"
          value={form.description}
          onChange={set("description")}
          fullWidth
          multiline
          rows={2}
        />
      </Grid>
      {showStatus && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={form.status || "ACTIVE"}
              label="Status"
              onChange={set("status")}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
}
